import {
	WizardStepId,
	WizardStepState,
	WizardStepStatus,
} from './types'

export type PhaseProgressStepId =
	| 'credentials'
	| 'metadata'
	| 'entities'
	| 'files'
	| 'mapping'
	| 'validation'
	| 'export'

export interface PhaseProgressStepSummary {
	stepId: PhaseProgressStepId
	label: string
	status: WizardStepState
	weight: number
	completed: boolean
}

export interface WeightedPhaseProgressSummary {
	steps: PhaseProgressStepSummary[]
	percentage: number
	completedWeight: number
	totalWeight: number
	completedSteps: number
	totalSteps: number
}

export const PHASE_PROGRESS_STEP_WEIGHTS: Record<PhaseProgressStepId, number> = {
	credentials: 10,
	metadata: 15,
	entities: 10,
	files: 15,
	mapping: 30,
	validation: 15,
	export: 5,
}

const PHASE_PROGRESS_STEP_ORDER: PhaseProgressStepId[] = [
	'credentials',
	'metadata',
	'entities',
	'files',
	'mapping',
	'validation',
	'export',
]

const PHASE_PROGRESS_STEP_LABELS: Record<PhaseProgressStepId, string> = {
	credentials: 'Configure SFSF Credentials',
	metadata: 'Load Metadata',
	entities: 'Select Entities',
	files: 'Select or Upload Files',
	mapping: 'Map Fields',
	validation: 'Validate Mappings and Data',
	export: 'Export Configuration',
}

const COMPLETED_STATES = new Set<WizardStepState>(['completed', 'skipped'])

function isCompletedStatus(status: WizardStepState): boolean {
	return COMPLETED_STATES.has(status)
}

export function calculateWeightedPhaseProgress(
	wizardSteps: WizardStepStatus[]
): WeightedPhaseProgressSummary {
	const stepMap = new Map<WizardStepId, WizardStepStatus>()
	for (const step of wizardSteps) {
		stepMap.set(step.stepId, step)
	}

	const steps = PHASE_PROGRESS_STEP_ORDER.map((stepId) => {
		const source = stepMap.get(stepId)
		const status: WizardStepState = source?.status || 'locked'
		const completed = isCompletedStatus(status)

		return {
			stepId,
			label: source?.label || PHASE_PROGRESS_STEP_LABELS[stepId],
			status,
			weight: PHASE_PROGRESS_STEP_WEIGHTS[stepId],
			completed,
		}
	})

	const totalWeight = steps.reduce((sum, step) => sum + step.weight, 0)
	const completedWeight = steps.reduce(
		(sum, step) => sum + (step.completed ? step.weight : 0),
		0
	)
	const completedSteps = steps.filter((step) => step.completed).length

	const percentage =
		totalWeight > 0 ? Math.round((completedWeight / totalWeight) * 100) : 0

	return {
		steps,
		percentage,
		completedWeight,
		totalWeight,
		completedSteps,
		totalSteps: steps.length,
	}
}
